import { useCallback, useEffect, useRef } from 'react';

import { useAccessToken } from '@/services/auth';

const MAX_BACKOFF_MS = 30000;

function toSocketUrl(path: string, token: string) {
  const url = new URL(
    `${import.meta.env.VITE_API_BASE_URL}${path}`,
    window.location.href,
  );

  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('token', token);

  return url.toString();
}

export type UseWebSocketOptions<TReceive> = {
  /** Path on the API, such as `/ws/presence`. The token is added to it. */
  path: string;
  onMessage: (data: TReceive) => void;
  onClose?: (event: CloseEvent) => void;
  /** Whether to reconnect after a close. Defaults to always. */
  retry?: boolean | number | ((attempt: number, event: CloseEvent) => boolean);
  /**
   * Delay before reconnecting, in ms. Return null to use `defaultRetryDelay`.
   */
  retryDelay?: number | ((attempt: number, event: CloseEvent) => number | null);
};

export type UseWebSocketResult<TSend> = {
  connect: () => void;
  disconnect: () => void;
  /** Returns false when the socket is not open and nothing was sent. */
  send: (data: TSend) => boolean;
};

function defaultRetryDelay(attempt: number) {
  return Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
}

/**
 * Authenticated JSON WebSocket that reconnects with backoff until `disconnect`
 * is called, and with the new token whenever the access token changes.
 */
export function useWebSocket<TSend, TReceive>(
  options: UseWebSocketOptions<TReceive>,
): UseWebSocketResult<TSend> {
  const token = useAccessToken();
  const tokenRef = useRef(token);
  const optionsRef = useRef(options);
  const socketRef = useRef<WebSocket | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const attemptRef = useRef(0);
  const wantedRef = useRef(false);

  useEffect(() => {
    optionsRef.current = options;
  });

  const open = useCallback(function open() {
    const token = tokenRef.current;

    if (!wantedRef.current || socketRef.current || !token) {
      return;
    }

    const scheduleRetry = (event: CloseEvent) => {
      const { retry = true, retryDelay = defaultRetryDelay } =
        optionsRef.current;
      const attempt = attemptRef.current;

      const shouldRetry =
        typeof retry === 'function'
          ? retry(attempt, event)
          : typeof retry === 'number'
            ? attempt < retry
            : retry;

      if (!shouldRetry) {
        return;
      }

      const delay =
        (typeof retryDelay === 'function'
          ? retryDelay(attempt, event)
          : retryDelay) ?? defaultRetryDelay(attempt);

      attemptRef.current++;
      retryTimerRef.current = setTimeout(open, delay);
    };

    const ws = new WebSocket(toSocketUrl(optionsRef.current.path, token));
    socketRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
    };

    ws.onmessage = (event) => {
      let data: TReceive;

      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      optionsRef.current.onMessage(data);
    };

    ws.onclose = (event) => {
      // Replaced or disconnected sockets don't retry
      if (socketRef.current !== ws) {
        return;
      }

      socketRef.current = null;
      optionsRef.current.onClose?.(event);

      if (!wantedRef.current) {
        return;
      }

      scheduleRetry(event);
    };
  }, []);

  useEffect(() => {
    tokenRef.current = token;

    if (!wantedRef.current) {
      return;
    }

    clearTimeout(retryTimerRef.current);
    attemptRef.current = 0;

    const old = socketRef.current;
    socketRef.current = null;
    old?.close(1000);

    open();
  }, [token, open]);

  const connect = useCallback(() => {
    wantedRef.current = true;
    open();
  }, [open]);

  const disconnect = useCallback(() => {
    wantedRef.current = false;
    clearTimeout(retryTimerRef.current);
    attemptRef.current = 0;

    socketRef.current?.close(1000);
    socketRef.current = null;
  }, []);

  const send = useCallback((data: TSend) => {
    const ws = socketRef.current;

    if (ws?.readyState !== WebSocket.OPEN) {
      return false;
    }

    ws.send(JSON.stringify(data));

    return true;
  }, []);

  useEffect(() => disconnect, [disconnect]);

  return { connect, disconnect, send };
}
